import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { SEO } from "../components/SEO";
import { Reveal, MaskLines } from "../components/Reveal";
import { api } from "../lib/api";
import { BLOG_CATEGORIES } from "../data/content";

export default function KnowledgeCentre() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (debounced.trim()) params.search = debounced.trim();
    api.get("/articles", { params })
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [category, debounced]);

  return (
    <div data-testid="knowledge-centre-page">
      <SEO
        title="Knowledge Centre"
        description="Articles and updates on income tax, GST, due dates, business compliance, financial planning, startup guidance, NRI taxation, capital gains and audit — from Neeraj Battan & Associates, Kurukshetra."
        path="/knowledge-centre"
      />

      <section className="relative bg-navy text-white overflow-hidden" data-testid="knowledge-hero">
        <div className="grain-overlay" />
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-24">
          <p className="eyebrow mb-6">Knowledge Centre</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1]" data-testid="knowledge-heading">
            <MaskLines lines={["Insights, Updates"]} />
            <MaskLines lines={["& Reminders."]} lineClassName="text-gold italic" baseDelay={0.3} />
          </h1>
          <Reveal delay={0.5} className="mt-7 max-w-2xl">
            <p className="text-slate-300 leading-relaxed">
              Articles and practical updates on taxation, GST, compliance and financial planning — written for general
              awareness of our clients and visitors.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col lg:flex-row lg:items-center gap-5 mb-10">
            <div className="relative w-full lg:max-w-sm">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles…"
                data-testid="blog-search-input"
                className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
              {["All", ...BLOG_CATEGORIES].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                    category === c ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-gold"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          {loading ? (
            <p className="text-slate-500 text-sm py-16 text-center" data-testid="blog-loading">Loading articles…</p>
          ) : articles.length === 0 ? (
            <div className="py-16 text-center" data-testid="blog-empty">
              <p className="font-serif text-2xl text-navy">No articles found</p>
              <p className="mt-2 text-sm text-slate-500">Try a different category or search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="blog-grid">
              {articles.map((a, i) => (
                <Reveal key={a.article_id} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/knowledge-centre/${a.slug}`}
                    data-testid={`article-card-${a.slug}`}
                    className="group block h-full rounded-2xl overflow-hidden border border-slate-200/80 bg-white hover:border-gold/50 hover:shadow-xl hover:shadow-navy/10 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                      {a.image_url && (
                        <img src={a.image_url} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="font-mono uppercase tracking-wider text-gold font-medium">{a.category}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="text-slate-400">{format(new Date(a.created_at), "dd MMM yyyy")}</span>
                      </div>
                      <h3 className="mt-3 font-serif text-xl font-semibold text-navy leading-snug group-hover:text-gold transition-colors">{a.title}</h3>
                      <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-3">{a.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy group-hover:text-gold transition-colors">
                        Read article <ArrowRight size={13} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
